import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../services/apiClient';

const ProgressMonitor: React.FC = () => {
  const { customerId, phaseId } = useParams<{ customerId: string; phaseId: string }>();

  const { data: progress, isLoading } = useQuery({
    queryKey: ['progress', customerId, phaseId],
    queryFn: () => apiClient.getProgress(customerId!, phaseId!),
    enabled: !!(customerId && phaseId),
    refetchInterval: 5000
  });

  const steps = progress?.steps || [];
  const overallProgress = progress?.overallProgress || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Progress Monitor</h1>
        <p className="text-gray-600 mt-2">Track your migration progress</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-6">Migration Steps</h2>

        <div className="space-y-4">
          {steps.map((step: any, index: number) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${step.completed ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                {step.completed ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                ) : (
                  <ClockIcon className="w-5 h-5 text-gray-400" />
                )}
              </div>

              <div className="ml-4">
                <h3 className={`text-lg font-medium ${step.completed ? 'text-green-900' : 'text-gray-900'
                  }`}>
                  {step.label}
                </h3>
                <p className="text-sm text-gray-600">
                  {step.completed ? 'Completed' : 'Pending'}
                </p>
              </div>
            </div>
          ))}
          {steps.length === 0 && (
            <div className="text-gray-500 italic">No progress steps available.</div>
          )}
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-blue-900 font-medium">Overall Progress</span>
            <span className="text-blue-900 font-bold">{overallProgress}%</span>
          </div>
          <div className="mt-2 bg-blue-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${overallProgress}%` }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressMonitor;
